import React from "react";
import PoweredByEaco from "./PoweredByEaco";

type PublicFormAvailability =
  | "OPEN"
  | "NOT_YET_OPEN"
  | "CLOSED"
  | "UNPUBLISHED";

interface ClosedViewProps {
  availability: PublicFormAvailability;
  startAt: string | null;
  timezone: string | null;
}

function formatInZone(iso: string, timezone: string | null): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "long",
      timeStyle: "short",
      timeZone: timezone || undefined,
    }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleString();
  }
}

/**
 * Shown when a public form is outside its availability window — either not
 * open yet (with the opening time, if known) or closed.
 */
const ClosedView: React.FC<ClosedViewProps> = ({
  availability,
  startAt,
  timezone,
}) => {
  const notYetOpen = availability === "NOT_YET_OPEN";
  const tzLabel = timezone ? ` (${timezone})` : "";

  // The link/QR were shared before the designer clicked Publish. Say so
  // plainly — "not found" sent people chasing a broken link.
  if (availability === "UNPUBLISHED") {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <div className="flex flex-1 items-center justify-center p-6 text-center">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold">This form isn’t live yet</h1>
            <p className="text-sm text-muted-foreground">
              Whoever shared this link hasn’t published the form. Ask them to
              publish it, then try the link again.
            </p>
          </div>
        </div>
        <PoweredByEaco />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 items-center justify-center p-6 text-center">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold">
            {notYetOpen ? "This form isn’t open yet" : "This form is closed"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {notYetOpen
              ? startAt
                ? `It opens on ${formatInZone(startAt, timezone)}${tzLabel}.`
                : "Please check back later."
              : "It’s no longer accepting responses."}
          </p>
        </div>
      </div>
      <PoweredByEaco />
    </div>
  );
};

export default ClosedView;
